import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isWebMode } from "@/shared/api";
import { RailLogo } from "@/shared/ui/RailLogo";
import { IC_CLASS } from "./RailButton";
import { RailTemaButton } from "./TemaButton";
import { RailLogoutButton } from "./RailLogoutButton";
import { useUnread } from "@/shared/unread/UnreadProvider";
import { useFattureDaFare } from "./useFattureDaFare";
import { RailCercaButton } from "./CercaRapida";

export type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  /**
   * Voci figlie: la voce non naviga piu' da sola, apre un sotto-menu (vedi
   * `RailSubmenu`). Le etichette qui possono essere lunghe — nel pannello
   * stanno in riga, non nel quadratino da 56px del rail.
   *
   * ⚠️ La BottomNav mobile ignora i figli di proposito: li' la voce continua a
   * portare alla pagina genitore, e le sotto-pagine si raggiungono dal link
   * nell'intestazione della pagina stessa.
   */
  children?: NavItem[];
  /** Zona del menu: decide la linea di separazione e il titolo nel foglio. */
  gruppo: GruppoNav;
  /** Nome esteso quando `label` e' abbreviata per il rail (solo «BI»). */
  nome?: string;
  /** Nome corto per la barra in basso del telefono («Ritiri e resi» → «Ritiri»). */
  breve?: string;
};

type RailItemProps = {
  item: NavItem;
  isActive: boolean;
  /** Conteggio badge (0 = nessun pallino). */
  badge?: number;
};

/**
 * I gruppi del menu (proposta menu 2026-10-28): prima il lavoro che scade in
 * giornata, poi le anagrafiche, poi i numeri. In fondo, staccati, gli
 * strumenti. Una linea sottile li separa nel rail e un titolo nel foglio
 * «Altro» del telefono: l'occhio trova la zona prima della voce.
 */
export type GruppoNav = "lavoro" | "anagrafiche" | "numeri" | "strumenti";

export const NOMI_GRUPPO: Record<GruppoNav, string> = {
  lavoro: "Lavoro di oggi",
  anagrafiche: "Clienti e fornitori",
  numeri: "Numeri",
  strumenti: "Strumenti",
};

export const navItems: NavItem[] = [
  // ── Lavoro di oggi: le code che fermano un ordine se nessuno le guarda.
  {
    label: "Ordini",
    path: "/ordini",
    gruppo: "lavoro",
    icon: (
      <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
      </svg>
    ),
  },
  {
    // Sotto-menu (Davide 28/10, «unire delle pagine come in fatturazione»):
    // la posta dei clienti e quella dei fornitori sotto la stessa voce.
    label: "Messaggi",
    path: "/messaggi",
    gruppo: "lavoro",
    icon: (
      <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
    children: [
      {
        label: "Messaggi clienti",
        path: "/messaggi",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        ),
      },
      {
        // Thread mail per-ordine verso i fornitori (Task 16, 2026-06-30). Il nome
        // dice «fornitori»: «Mail» da solo si confondeva coi Messaggi dei clienti.
        label: "Mail fornitori",
        path: "/mail",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <path d="M22 6l-10 7L2 6" />
          </svg>
        ),
      },
    ],
  },
  {
    // Portale fatturazione (spec 2026-07-14): sostituisce il GSheet.
    // Dal 2026-09-07 e' un sotto-menu: la fatturazione vera, i corrispettivi
    // Amazon e le fatture estere sono lavori diversi sotto la stessa voce.
    label: "Fatture",
    path: "/fatturazione",
    gruppo: "lavoro",
    icon: (
      <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
      </svg>
    ),
    children: [
      {
        label: "Fatturazione",
        path: "/fatturazione",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
          </svg>
        ),
      },
      {
        // Il file mensile per il commercialista. Icona a griglia: e' un foglio
        // di calcolo, ed e' l'unica voce del rail che produce un allegato.
        label: "Corrispettivi Amazon",
        path: "/fatturazione/corrispettivi",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M3 9h18M9 9v11" />
          </svg>
        ),
      },
      {
        // Le fatture dei fornitori esteri (Shopify, Google, Meta...) da cui
        // Annalisa fa le autofatture. Icona a globo: e' l'estero.
        label: "Fatture estere",
        path: "/fatturazione/fatture-estere",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
          </svg>
        ),
      },
      {
        // L'archivio delle fatture di acquisto scaricate da Eureka (30/10/2026).
        // Icona a scatola: e' la merce che compriamo.
        label: "Fatture fornitori",
        path: "/fatturazione/fatture-fornitori",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 7l9-4 9 4v10l-9 4-9-4z" />
            <path d="M3 7l9 4 9-4M12 11v10" />
          </svg>
        ),
      },
      {
        // Gli errori che le fatture fanno vedere, fornitori e SdI (30/10/2026).
        // Icona a scudo con la spunta: e' un controllo.
        label: "Controlli fatture",
        path: "/fatturazione/controlli",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
            <path d="M8.5 12l2.5 2.5 4.5-5" />
          </svg>
        ),
      },
      {
        // Il saldo sul conto di ogni marketplace e dei pagamenti Kery (Davide
        // 28/10/2026). Icona a portafoglio: sono soldi nostri ancora fuori.
        label: "Saldi",
        path: "/fatturazione/saldi",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 7a2 2 0 0 1 2-2h12v4" />
            <path d="M4 7v11a2 2 0 0 0 2 2h14V9H6a2 2 0 0 1-2-2z" />
            <path d="M16 14.5h.01" />
          </svg>
        ),
      },
    ],
  },
  {
    // Ritiri del corriere e resi: tutti e due sono pacchi che tornano o
    // partono da un magazzino, e i resi hanno il loro ritiro.
    label: "Ritiri e resi",
    breve: "Ritiri",
    path: "/ritiri",
    gruppo: "lavoro",
    icon: (
      <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7" />
        <circle cx="7" cy="18" r="2" />
        <circle cx="17" cy="18" r="2" />
      </svg>
    ),
    children: [
      {
        // Ritiri SDA (2026-09-23): prenotazione del corriere per magazzino, entro
        // le 11:50 per il ritiro in giornata. Per questo sta nel lavoro di oggi.
        label: "Ritiri corriere",
        path: "/ritiri",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7" />
            <circle cx="7" cy="18" r="2" />
            <circle cx="17" cy="18" r="2" />
          </svg>
        ),
      },
      {
        label: "Resi",
        path: "/resi",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 7v6h6M3 13a9 9 0 1 0 3-7.7L3 8" />
          </svg>
        ),
      },
      {
        // Pratiche di rimborso con Bracchi lette dalle mail (Davide 03/11/2026).
        // Icona a scudo col punto esclamativo: un reclamo aperto.
        label: "Reclami corriere",
        path: "/reclami",
        gruppo: "lavoro",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
            <path d="M12 8v5M12 16h.01" />
          </svg>
        ),
      },
    ],
  },
  // ── Anagrafiche e numeri.
  {
    // Le due anagrafiche: chi compra e chi vende a noi.
    label: "Anagrafiche",
    path: "/clienti",
    gruppo: "anagrafiche",
    icon: (
      <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    children: [
      {
        // Pagina Clienti (item 9, mega-batch10): lista aggregata + scheda 360.
        label: "Clienti",
        path: "/clienti",
        gruppo: "anagrafiche",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        ),
      },
      {
        // Pagina Fornitori UNIFICATA a tab (decisione Davide 2026-07-03): assorbe
        // anche il vecchio Catalogo (/catalogo redirige qui). Icona: un capannone,
        // non piu' il camion — il camion era identico a quello dei Ritiri.
        label: "Fornitori",
        path: "/fornitori",
        gruppo: "anagrafiche",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 21V9l9-6 9 6v12" />
            <path d="M7 21v-8h10v8M7 17h10" />
          </svg>
        ),
      },
    ],
  },
  {
    // Ex tab Dashboard, ridisegnata come Business Intelligence sul margine
    // (R6 2026-07-03). Label corta "BI"; il titolo esteso vive nella pagina.
    label: "BI",
    nome: "Business Intelligence",
    path: "/dashboard",
    gruppo: "numeri",
    icon: (
      <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 13h8V3H3zM13 21h8V11h-8zM3 21h8v-6H3zM13 9h8V3h-8z" />
      </svg>
    ),
  },
  // ── Strumenti: in fondo al rail, vicino a tema ed Esci.
  {
    // Impostazioni e la guida d'uso: gli strumenti che si aprono di rado.
    label: "Impostazioni",
    path: "/impostazioni",
    gruppo: "strumenti",
    icon: (
      <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    children: [
      {
        label: "Impostazioni",
        path: "/impostazioni",
        gruppo: "strumenti",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        ),
      },
      {
        label: "Istruzioni",
        path: "/istruzioni",
        gruppo: "strumenti",
        icon: (
          <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
          </svg>
        ),
      },
    ],
  },
];

/** Il nome per intero: nel foglio del telefono, nella ricerca, nei titoli. */
export function nomeVoce(item: NavItem): string {
  return item.nome ?? item.label;
}

/** Geometria e stati del quadratino del rail, condivisi da voce e sotto-menu. */
function classiTassello(isActive: boolean): string {
  return [
    "relative w-[68px] px-1 py-1.5 rounded-md",
    "flex flex-col items-center gap-1",
    // Due righe al massimo («Mail fornitori», «Impostazioni»): niente piu'
    // nomi troncati come «Imposta» o «Istruz.».
    "text-center text-[10.5px] font-medium leading-[1.15] transition-colors duration-[120ms]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
    // Inattive a 75%: a 55% sul fondo scuro si leggevano a fatica (regola del
    // testo che si deve vedere bene, Davide 23/09).
    isActive ? "bg-white/[0.1] text-white" : "text-white/75 hover:bg-white/[0.06] hover:text-white",
  ].join(" ");
}

/**
 * Pallino rosso col numero sull'icona (0 = niente pallino).
 * `intero`: niente tetto «9+» — sulle fatture il numero vero decide quanto
 * lavoro c'e' (Davide 16/09); sui Messaggi basta sapere che ce ne sono tanti.
 */
export function BadgeConteggio({ n, intero = false }: { n: number; intero?: boolean }) {
  if (n <= 0) return null;
  return (
    <span
      data-badge
      aria-hidden="true"
      className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red px-1 text-[10px] font-bold leading-none text-white"
    >
      {!intero && n > 9 ? "9+" : n}
    </span>
  );
}

export function RailItem({ item, isActive, badge = 0 }: RailItemProps) {
  const navigate = useNavigate();
  const ariaLabel = badge > 0 ? `${item.label}, ${badge} non letti` : item.label;

  return (
    <button
      aria-label={ariaLabel}
      onClick={() => {
        void navigate(item.path);
      }}
      className={classiTassello(isActive)}
    >
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute left-[-1px] top-2 bottom-2 w-[2px] bg-accent rounded-[0_2px_2px_0]"
        />
      )}
      <span className="relative">
        {item.icon}
        <BadgeConteggio n={badge} />
      </span>
      {item.label}
    </button>
  );
}

/**
 * Voce del rail che invece di navigare apre un pannello con le sue sotto-voci.
 *
 * Il pannello e' `fixed` e posizionato dal rettangolo del pulsante, non
 * `absolute`: il rail ha `overflow-y-auto`, e per le regole CSS un asse non
 * `visible` rende `auto` anche l'altro — un figlio assoluto largo 190px
 * verrebbe tagliato dalla colonna da 80px.
 */
function RailSubmenu({
  item,
  isActive,
  badge = 0,
  badgeIntero = false,
}: RailItemProps & { badgeIntero?: boolean }) {
  const figli = item.children ?? [];
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [aperto, setAperto] = useState(false);
  const [posizione, setPosizione] = useState<{ top: number; left: number } | null>(null);
  const bottoneRef = useRef<HTMLButtonElement>(null);

  // Cambio rotta = menu chiuso, anche quando a navigare e' stato qualcun altro.
  useEffect(() => {
    setAperto(false);
  }, [pathname]);

  useEffect(() => {
    if (!aperto) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setAperto(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aperto]);

  function alterna() {
    const r = bottoneRef.current?.getBoundingClientRect();
    if (r) {
      // Il pannello e' alto quanto i figli: su una finestra bassa lo si
      // risale invece di lasciarlo uscire dallo schermo.
      const alto = figli.length * 40 + 16;
      setPosizione({
        top: Math.max(8, Math.min(r.top, window.innerHeight - alto - 8)),
        left: r.right + 6,
      });
    }
    setAperto((v) => !v);
  }

  return (
    <>
      <button
        ref={bottoneRef}
        type="button"
        aria-label={
          badge > 0
            ? `${item.label}, ${badge} ${badgeIntero ? "da fare" : "non letti"}`
            : item.label
        }
        aria-haspopup="menu"
        aria-expanded={aperto}
        onClick={alterna}
        className={classiTassello(isActive || aperto)}
      >
        {isActive && (
          <span
            aria-hidden="true"
            className="absolute left-[-1px] top-2 bottom-2 w-[2px] bg-accent rounded-[0_2px_2px_0]"
          />
        )}
        {/* Freccina: senza, niente dice che la voce si apre invece di andare. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className={`absolute right-1 top-[18px] h-2.5 w-2.5 fill-none stroke-current stroke-[2.4px] [stroke-linecap:round] [stroke-linejoin:round] transition-transform duration-150 ${aperto ? "translate-x-0.5" : ""}`}
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
        <span className="relative">
          {item.icon}
          <BadgeConteggio n={badge} intero={badgeIntero} />
        </span>
        {item.label}
      </button>

      {aperto && posizione && (
        <>
          {/* Stessa disciplina del foglio "Altro" della BottomNav: uno sfondo
              cliccabile chiude, e tiene il click fuori dalla pagina sotto. */}
          <button
            type="button"
            aria-label="Chiudi"
            onClick={() => setAperto(false)}
            className="fixed inset-0 z-[9989] cursor-default"
          />
          <div
            role="menu"
            aria-label={item.label}
            style={{ top: posizione.top, left: posizione.left }}
            className="fixed z-[9990] min-w-[200px] rounded-md border border-white/10 bg-rail py-1 shadow-popover"
          >
            {figli.map((figlio) => {
              const attivo = figlio.path === figlioAttivo(pathname, figli);
              return (
                <button
                  key={figlio.path}
                  type="button"
                  role="menuitem"
                  aria-current={attivo ? "page" : undefined}
                  onClick={() => {
                    setAperto(false);
                    void navigate(figlio.path);
                  }}
                  className={[
                    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px]",
                    "transition-colors duration-[120ms]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset",
                    attivo
                      ? "bg-white/[0.08] text-white"
                      : "text-white/70 hover:bg-white/[0.06] hover:text-white",
                  ].join(" ")}
                >
                  {figlio.icon}
                  {figlio.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

/** Il path della voce in cui si e' adesso, o null. Vince il path piu' lungo:
 *  `/fatturazione` e' prefisso di `/fatturazione/corrispettivi`, e col solo
 *  `startsWith` sarebbero accese tutte e due; `/mail/:ref` e `/clienti/:key`
 *  invece devono accendere la loro voce anche sulla sotto-rotta. */
export function figlioAttivo(pathname: string, figli: NavItem[]): string | null {
  const presi = figli
    .map((f) => f.path)
    .filter((p) => pathname === p || pathname.startsWith(p + "/"))
    .sort((a, b) => b.length - a.length);
  return presi[0] ?? null;
}

/** Linea sottile fra un gruppo e l'altro del rail. */
function Separatore() {
  return <div aria-hidden="true" className="my-1.5 h-px w-9 shrink-0 bg-white/[0.12]" />;
}

export function Rail() {
  const { pathname } = useLocation();
  const { unreadThreads } = useUnread();
  const fattureDaFare = useFattureDaFare();

  function voce(item: NavItem) {
    const attiva = figlioAttivo(pathname, [item, ...(item.children ?? [])]) !== null;
    return item.children ? (
      <RailSubmenu
        key={item.path}
        item={item}
        isActive={attiva}
        badge={
          item.path === "/fatturazione"
            ? fattureDaFare
            : item.path === "/messaggi"
              ? unreadThreads
              : 0
        }
        badgeIntero={item.path === "/fatturazione"}
      />
    ) : (
      <RailItem
        key={item.path}
        item={item}
        isActive={attiva}
        badge={item.path === "/messaggi" ? unreadThreads : 0}
      />
    );
  }

  const del = (g: GruppoNav) => navItems.filter((i) => i.gruppo === g).map(voce);

  // `overflow-y-auto`: su una finestra bassa l'ultima voce — «Esci» — finiva
  // sotto il bordo, tagliata da un rail che non scorreva (Davide 2026-08-24).
  // La barra di scorrimento e' nascosta: in 80px si mangerebbe un quinto.
  return (
    <aside
      aria-label="Menu"
      className="bg-rail text-ink-4 flex min-h-0 flex-col items-center gap-0.5 overflow-y-auto py-[14px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <div className="mb-3">
        <RailLogo size={44} />
      </div>

      <RailCercaButton />
      <Separatore />
      {del("lavoro")}
      <Separatore />
      {del("anagrafiche")}
      {del("numeri")}

      <div className="min-h-4 flex-1" />

      {del("strumenti")}
      <div className="mt-1 flex gap-1">
        <RailTemaButton compatto />
        {/* "Esci" = chiude la SESSIONE applicativa (logout + redirect a /login).
            Fuori da web-mode (dev) l'auth non e' montata: niente da chiudere. */}
        {isWebMode() && <RailLogoutButton compatto />}
      </div>
    </aside>
  );
}
